import {useCallback, useContext, useEffect, useState} from 'react'
import { notifyError } from '../../../../utils/notifications'
import {Col, Flex, Form, Modal, Row, Typography} from 'antd'
import {useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {getRequest, postRequest, putRequest} from '../../../../services/base'
import {NavContext} from '../../../../context/global_context'
import {
  DeviceConnectionContext,
  SET_SELECTED_DEVICE_CONNECTION_DATA,
  SET_SHOW_HIDE_DEVICE_CONNECTION_POPUP,
} from '../context/context'
import SelectInput from '../../../common/SelectInput'
import Button from '../../../common/Button'
import useDebouncedValue from '../../../../utils/common_function'

const DeviceConnectionFormModal = ({getDeviceConnectionsData}) => {
  const intl = useIntl()
  const {vehicle} = useParams()
  const [state, dispatch] = useContext(DeviceConnectionContext)
  const [, navDispatch] = useContext(NavContext)
  const [searchFailed, setSearchFailed] = useState(false)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  const [deviceOptions, setDeviceOptions] = useState([])
  const [deviceOptionsLoading, setDeviceOptionsLoading] = useState(false)
  const [deviceSearchTerm, setDeviceSearchTerm] = useState('')
  const debouncedDeviceSearchTerm = useDebouncedValue(deviceSearchTerm, 500)
  const [signalOptions, setSignalOptions] = useState([])
  const [aliasesOptions, setAliasesOptions] = useState([])
  const [fromPinsOptions, setFromPinsOptions] = useState([])
  const [toPinsOptions, setToPinsOptions] = useState([])

  const fetchOptions = useCallback(async () => {
    const signalRes = await getRequest(
      `/api/device_connection/signal_options/?vehicle=${vehicle}`
    )
    if (signalRes?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'generic.optionsLoadError' }))
      return
    }
    setSignalOptions(signalRes?.data || [])
  }, [vehicle])

  useEffect(() => {
    fetchOptions()
  }, [fetchOptions])

  const getDeviceOptions = async (search = '') => {
    setDeviceOptionsLoading(true)
    try {
      let res = await getRequest(
        `/api/devices/?vehicle=${vehicle}&search=${search}`
      )
      // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
      if (res?.status === false) {
        setSearchFailed(true)
        setDeviceOptions([])
        return
      }
      setSearchFailed(false)
      setDeviceOptions(
        (res?.results || []).map((d) => ({value: d.id, label: d.name}))
      )
    } finally {
      setDeviceOptionsLoading(false)
    }
  }

  useEffect(() => {
    if (debouncedDeviceSearchTerm) {
      getDeviceOptions(debouncedDeviceSearchTerm)
    }
  }, [debouncedDeviceSearchTerm])

  const getDeviceAliases = async (deviceName) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({
          id: 'deviceConnections.fetchingDeviceAlias',
        }),
        showLoader: true,
      },
      notification: {},
    })
    let res = await getRequest(
      `/api/device_connection/aliases/?vehicle=${vehicle}&device_name=${deviceName}`
    )
    navDispatch({ type: 'loader', data: {}, notification: {} })
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'deviceConnections.aliasesLoadError' }))
      return
    }
    setAliasesOptions(res?.data || [])
  }

  const getPins = async (connectorLabel, type) => {
    let res = await getRequest(
      `/api/device_connection/pins/?vehicle=${vehicle}&alias=${connectorLabel}&type=${type}`
    )
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'deviceConnections.pinsLoadError' }))
      return
    }
    if (type === 'from') {
      setFromPinsOptions(res?.data || [])
    } else {
      setToPinsOptions(res?.data || [])
    }
  }

  const closeFormPopup = () => {
    setFromPinsOptions([])
    setToPinsOptions([])
    dispatch({type: SET_SHOW_HIDE_DEVICE_CONNECTION_POPUP, data: false})
    form.resetFields()
  }

  const saveDeviceConnection = async (values) => {
    setSaving(true)

    const selected = state?.selectedObject || {}
    const formData = new FormData()
    formData.append('import_file', selected.import_file || '')
    formData.append('vehicle', vehicle)
    formData.append('device', values.device_data || selected.device || '')
    formData.append('pin_from', selected.pin_from || values.pin_from || '')
    formData.append('pin_to', selected.pin_to || values.pin_to || '')
    formData.append('signal_name', selected.signal_obj?.value || selected.signal || values.signal_data || '')
    formData.append('signal', selected.signal_obj?.label || '')

    let res = null
    if (selected?.id) {
      res = await putRequest(
        `/api/device_connection/${selected.id}/?vehicle=${vehicle}`,
        formData
      )
    } else {
      res = await postRequest(`/api/device_connection/`, formData)
    }

    setSaving(false)

    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({
            id: 'deviceConnections.saveSuccess',
          }),
        },
      })
      getDeviceConnectionsData()
      closeFormPopup()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({id: 'deviceConnections.saveError'}),
        },
      })
    }
  }

  // Initialize form when opening — seed options with current values, then load full list
  useEffect(() => {
    if (!state.showDeviceConnFormPopup) return

    const obj = state?.selectedObject || {}

    // Seed options with the currently selected item so Select can render the label immediately
    if (obj?.device_data?.id) {
      setDeviceOptions([{value: obj.device_data.id, label: obj.device_data.name}])
      getDeviceOptions('') // load full list in background
    }
    if (obj?.device_data?.name) {
      getDeviceAliases(obj.device_data.name) // load full list in background
    }
    if (obj?.from_alias?.label) {
      setAliasesOptions(prev => {
        const exists = prev.some(o => o.value === obj.from_alias.value || o.label === obj.from_alias.label)
        if (!exists) {
          return [...prev, {value: obj.from_alias.value || obj.from_alias.label, label: obj.from_alias.label}]
        }
        return prev
      })
    }
    if (obj?.to_alias?.label) {
      setAliasesOptions(prev => {
        const exists = prev.some(o => o.value === obj.to_alias.value || o.label === obj.to_alias.label)
        if (!exists) {
          return [...prev, {value: obj.to_alias.value || obj.to_alias.label, label: obj.to_alias.label}]
        }
        return prev
      })
    }
    // Seed pin options with current selections
    if (obj?.pin_from_obj) {
      setFromPinsOptions([obj.pin_from_obj])
    }
    if (obj?.pin_to_obj) {
      setToPinsOptions([obj.pin_to_obj])
    }
    if (obj?.from_alias?.label) {
      getPins(obj.from_alias.label, 'from')
    }
    if (obj?.to_alias?.label) {
      getPins(obj.to_alias.label, 'to')
    }

    // Set form field values
    form.setFieldsValue({
      device_data: obj.device_data?.id || obj.device || undefined,
      signal_data: obj.signal_obj?.value || obj.signal_obj?.id || undefined,
      from_aliases: obj.from_alias?.value || obj.from_alias?.label || undefined,
      pin_from: obj.pin_from_obj?.value || obj.pin_from?.split('-').pop() || undefined,
      to_aliases: obj.to_alias?.value || obj.to_alias?.label || undefined,
      pin_to: obj.pin_to_obj?.value || obj.pin_to?.split('-').pop() || undefined,
    })
  }, [state.showDeviceConnFormPopup])

  return (
    <Modal
      open={state.showDeviceConnFormPopup}
      title={intl.formatMessage({
        id: state?.selectedObject?.id
          ? 'deviceConnections.editDeviceConnection'
          : 'deviceConnections.newDeviceConnection',
      })}
      width="60%"
      className="device-connections-form-modal"
      centered
      footer={null}
      closable
      onCancel={closeFormPopup}
    >
      <div className="container">
        <Form form={form} layout="vertical" onFinish={saveDeviceConnection}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <SelectInput
                name="device_data"
                label={intl.formatMessage({
                  id: 'deviceConnections.deviceName',
                })}
                options={deviceOptions}
                notFoundContent={
                  searchFailed ? (
                    <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                      {intl.formatMessage({ id: 'generic.searchFailed' })}
                    </Typography.Text>
                  ) : undefined
                }
                isRequired={true}
                showLoader={deviceOptionsLoading}
                onInputChange={(value) => {
                  setDeviceSearchTerm(value || '')
                }}
                onMenuOpen={() => {
                  if (deviceOptions?.length < 1) getDeviceOptions('')
                }}
                onChange={(value) => {
                  dispatch({
                    type: SET_SELECTED_DEVICE_CONNECTION_DATA,
                    data: {
                      ...state?.selectedObject,
                      device_data: value,
                      device: value?.id || value?.value,
                      from_alias: null,
                      pin_from_obj: null,
                      pin_from: null,
                      to_alias: null,
                      pin_to_obj: null,
                      pin_to: null,
                    },
                  })
                  form.setFieldsValue({
                    from_aliases: undefined,
                    pin_from: undefined,
                    to_aliases: undefined,
                    pin_to: undefined,
                  })
                  setFromPinsOptions([])
                  setToPinsOptions([])
                  if (value?.label) {
                    getDeviceAliases(value.label)
                  } else {
                    setAliasesOptions([])
                  }
                }}
              />
            </Col>

            <Col span={12}>
              <SelectInput
                name="signal_data"
                label={intl.formatMessage({
                  id: 'deviceConnections.signalName',
                })}
                options={signalOptions}
                isRequired={true}
                allowClear
                onChange={(value) => {
                  dispatch({
                    type: SET_SELECTED_DEVICE_CONNECTION_DATA,
                    data: {
                      ...state?.selectedObject,
                      signal: value?.value || '',
                      signal_obj: value,
                    },
                  })
                }}
              />
            </Col>

            <Col span={12}>
                <SelectInput
                  name="from_aliases"
                  label={intl.formatMessage({
                    id: 'deviceConnections.fromAliases',
                  })}
                  options={aliasesOptions}
                  isRequired
                  disabled={!state?.selectedObject?.device}
                  onChange={(value) => {
                    if (value?.label) {
                      dispatch({
                        type: SET_SELECTED_DEVICE_CONNECTION_DATA,
                        data: {
                          ...state?.selectedObject,
                          from_alias: value,
                          pin_from_obj: null,
                          pin_from: null,
                        },
                      })
                      getPins(value.label, 'from')
                    } else {
                      dispatch({
                        type: SET_SELECTED_DEVICE_CONNECTION_DATA,
                        data: {
                          ...state?.selectedObject,
                          pin_from_obj: null,
                          pin_from: null,
                          from_alias: null,
                        },
                      })
                      setFromPinsOptions([])
                    }
                  }}
                />
            </Col>

            <Col span={12}>
                <SelectInput
                  name="pin_from"
                  label={intl.formatMessage({
                    id: 'deviceConnections.pinFrom',
                  })}
                  options={fromPinsOptions}
                  isRequired
                  disabled={!state?.selectedObject?.from_alias?.label}
                  onChange={(value) => {
                    dispatch({
                      type: SET_SELECTED_DEVICE_CONNECTION_DATA,
                      data: {
                        ...state?.selectedObject,
                        pin_from_obj: value,
                        pin_from: value?.label
                          ? `${state?.selectedObject?.from_alias?.label}-${value?.label}`
                          : null,
                      },
                    })
                  }}
                  onMenuOpen={() => {
                    if (
                      fromPinsOptions?.length < 1 &&
                      state?.selectedObject?.from_alias?.label
                    ) {
                      getPins(
                        state?.selectedObject?.from_alias?.label,
                        'from'
                      )
                    }
                  }}
                />
            </Col>

            <Col span={12}>
                <SelectInput
                  name="to_aliases"
                  label={intl.formatMessage({
                    id: 'deviceConnections.toAliases',
                  })}
                  options={aliasesOptions}
                  isRequired
                  disabled={!state?.selectedObject?.device}
                  onChange={(value) => {
                    if (value?.label) {
                      dispatch({
                        type: SET_SELECTED_DEVICE_CONNECTION_DATA,
                        data: {
                          ...state?.selectedObject,
                          to_alias: value,
                          pin_to_obj: null,
                          pin_to: null,
                        },
                      })
                      getPins(value.label, 'to')
                    } else {
                      dispatch({
                        type: SET_SELECTED_DEVICE_CONNECTION_DATA,
                        data: {
                          ...state?.selectedObject,
                          pin_to_obj: null,
                          pin_to: null,
                          to_alias: null,
                        },
                      })
                      setToPinsOptions([])
                    }
                  }}
                />
            </Col>

            <Col span={12}>
                <SelectInput
                  name="pin_to"
                  label={intl.formatMessage({
                    id: 'deviceConnections.pinTo',
                  })}
                  options={toPinsOptions}
                  isRequired
                  disabled={!state?.selectedObject?.to_alias?.label}
                  onChange={(value) => {
                    dispatch({
                      type: SET_SELECTED_DEVICE_CONNECTION_DATA,
                      data: {
                        ...state?.selectedObject,
                        pin_to_obj: value,
                        pin_to: value?.label
                          ? `${state?.selectedObject?.to_alias?.label}-${value?.label}`
                          : null,
                      },
                    })
                  }}
                  onMenuOpen={() => {
                    if (
                      toPinsOptions?.length < 1 &&
                      state?.selectedObject?.to_alias?.label
                    ) {
                      getPins(
                        state?.selectedObject?.to_alias?.label,
                        'to'
                      )
                    }
                  }}
                />
            </Col>

            <Col span={24}>
              <Flex justify="end">
                <Button htmlType="submit" type="primary" loading={saving}>
                  {intl.formatMessage({
                    id: 'deviceConnections.save',
                  })}
                </Button>
              </Flex>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  )
}

export default DeviceConnectionFormModal
